import React, { useEffect, useMemo, useState } from 'react';
import ModelsAPI from '../../api/models';
import { ModelConstraintsResponse } from '../../api/structs/model';
import { MediaOutputSpec } from '../../api/structs/media-spec';
import { Button } from '../ui';
import { ConstraintFieldInput } from './modelSettingsFields';
import {
  getVisibleFields,
  buildDefaultSettings,
  outputSpecToSettings,
  settingsToOutputSpec,
  validateSettings,
} from './modelSettingsHelpers';

interface ModelSettingsSectionProps {
  modelId: string;
  modality: 'image' | 'video' | 'audio';
  settings: Partial<MediaOutputSpec>;
  onSettingsChange: (settings: Partial<MediaOutputSpec>) => void;
  title?: string;
  description?: string;
}

const ModelSettingsSection: React.FC<ModelSettingsSectionProps> = ({
  modelId,
  modality,
  settings,
  onSettingsChange,
  title = 'Advanced settings',
  description = 'Show model-specific fields like dimensions, duration and provider settings.',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [constraints, setConstraints] = useState<ModelConstraintsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localSettings, setLocalSettings] = useState<Partial<MediaOutputSpec>>({});

  useEffect(() => {
    if (!isExpanded || !modelId) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    ModelsAPI.getModelConstraints(modelId, modality)
      .then((data) => {
        if (cancelled) return;
        setConstraints(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(`Failed to load constraints: ${err.message}`);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isExpanded, modelId, modality]);

  useEffect(() => {
    if (!isExpanded) return;
    if (!constraints) {
      setLocalSettings(settings);
      return;
    }

    const defaults = buildDefaultSettings(constraints);
    const merged = { ...defaults, ...outputSpecToSettings(settings, constraints) };
    setLocalSettings(validateSettings(merged, constraints));
  }, [constraints, isExpanded, settings]);

  const visibleFields = constraints ? getVisibleFields(constraints) : [];

  const providerSettingEntries = useMemo(() => {
    if (!constraints?.provider_settings) return [];
    const entries: Array<{ providerKey: string; settingKey: string; options: unknown[]; label: string }> = [];
    for (const [providerKey, providerSchema] of Object.entries(constraints.provider_settings)) {
      if (typeof providerSchema !== 'object' || providerSchema === null) continue;
      for (const [settingKey, options] of Object.entries(providerSchema as Record<string, unknown>)) {
        if (Array.isArray(options)) {
          entries.push({ providerKey, settingKey, options, label: `${providerKey} ${settingKey}` });
        }
      }
    }
    return entries;
  }, [constraints?.provider_settings]);

  const handleFieldChange = (key: string, value: unknown) => {
    setLocalSettings((prev) => {
      const next = { ...prev, [key]: value };
      return constraints ? validateSettings(next, constraints) : next;
    });
  };

  const handleProviderSettingChange = (providerKey: string, settingKey: string, value: unknown) => {
    setLocalSettings((prev) => {
      const prevPs = ((prev as Record<string, unknown>).provider_settings as Record<string, unknown>) || {};
      const prevProvider = (prevPs[providerKey] as Record<string, unknown>) || {};
      const nextPs = {
        ...prevPs,
        [providerKey]: { ...prevProvider, [settingKey]: value },
      };
      const next = { ...prev, provider_settings: nextPs };
      return constraints ? validateSettings(next, constraints) : next;
    });
  };

  const handleApply = () => {
    onSettingsChange(settingsToOutputSpec(localSettings));
  };

  const handleReset = () => {
    if (constraints) {
      setLocalSettings(buildDefaultSettings(constraints));
    }
  };

  return (
    <div className="space-y-3 rounded border border-white/10 bg-black/20 p-3">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 text-left"
        onClick={() => setIsExpanded((value) => !value)}
      >
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-zinc-200 font-medium">{title}</div>
          <div className="text-[11px] text-zinc-500 mt-1">{description}</div>
        </div>
        <span className="text-zinc-400 text-sm">{isExpanded ? '▾' : '▸'}</span>
      </button>

      {isExpanded && (
        <div className="space-y-4 pt-2 border-t border-white/10">
          {!modelId && (
            <div className="text-sm text-zinc-500">
              Select a model first to load advanced settings.
            </div>
          )}

          {isLoading && (
            <div className="py-3 text-sm text-zinc-400">Loading constraints...</div>
          )}

          {error && (
            <div className="rounded border border-white/10 bg-black/40 p-3 text-sm text-zinc-200">
              {error}
            </div>
          )}

          {!isLoading && !error && visibleFields.length > 0 && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {visibleFields.map(({ key, field }) => (
                <ConstraintFieldInput
                  key={key}
                  fieldKey={key}
                  field={field}
                  value={(localSettings as Record<string, unknown>)[key]}
                  onChange={handleFieldChange}
                />
              ))}
            </div>
          )}

          {!isLoading && !error && providerSettingEntries.length > 0 && (
            <div className="grid grid-cols-1 gap-3 border-t border-white/10 pt-3 md:grid-cols-2 xl:grid-cols-3">
              {providerSettingEntries.map(({ providerKey, settingKey, options, label }) => {
                const currentPs = ((localSettings as Record<string, unknown>).provider_settings as Record<string, unknown>) || {};
                const currentProvider = (currentPs[providerKey] as Record<string, unknown>) || {};
                const value = currentProvider[settingKey] ?? options[0];

                return (
                  <div key={`${providerKey}.${settingKey}`}>
                    <label className="mb-1 block text-xs font-medium text-slate-400">{label}</label>
                    <select
                      className="input text-sm"
                      value={String(value)}
                      onChange={(event) => handleProviderSettingChange(providerKey, settingKey, event.target.value)}
                    >
                      {options.map((opt) => (
                        <option key={String(opt)} value={String(opt)}>
                          {String(opt)}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          )}

          {!isLoading && !error && visibleFields.length === 0 && providerSettingEntries.length === 0 && constraints && (
            <div className="py-3 text-sm text-zinc-500">No configurable settings for this model.</div>
          )}

          <div className="flex justify-between border-t border-white/10 pt-2">
            <Button variant="ghost" size="sm" onClick={handleReset} disabled={!constraints}>
              Reset Defaults
            </Button>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(false)}
              >
                Hide
              </Button>
              <Button size="sm" onClick={handleApply} disabled={!modelId || (!constraints && !localSettings)}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelSettingsSection;
